// Answer controls for "several boxes" questions (2026-10-12): 2 to 5 small
// typed-number boxes in one question. Like ListAnswers.tsx, they are
// controlled by the practice screen's own answer text (one line per box,
// see boxesAnswerText.ts), so the screen's existing reset-per-question and
// retry-with-the-same-key behaviour applies to them unchanged. They send
// {responseKind: 'ordered_set', value: {order: ["a=12", "b=3.5"]}}.
//
// Two layouts. BoxesInSentence: the prompt marks where each box goes
// ("[a]/15") and the box is typed into right there -- as the top or the
// bottom of a stacked fraction when that is where its marker is.
// LabelledBoxes: the boxes sit under the prompt, each with its own label.

import { FractionStack, MathText } from '../../components/MathText';
import {
  splitBoxFractions,
  splitBoxMarkers,
} from '../../platform/content/boxAnswer';
import {
  BOX_MAX_LENGTH,
  boxName,
  boxTextInvalid,
  joinBoxValues,
  splitBoxValues,
  type AnswerBox,
} from './boxesAnswerText';

type BoxesProps = {
  boxes: AnswerBox[];
  value: string;
  // The box the number pad types into: the one last tapped or tabbed to.
  activeId: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onActive: (id: string) => void;
  // Enter in the last box.
  onSubmit: () => void;
};

function boxInputId(id: string): string {
  return `practice-box-${id}`;
}

// A real, focusable, keyboard/screen-reader-operable input, same as the
// single number box. type="text", not "number", so a minus sign and a
// decimal point can both be typed from a keyboard.
function BoxInput({
  box,
  inSentence,
  stacked = false,
  boxes,
  value,
  activeId,
  disabled,
  onChange,
  onActive,
  onSubmit,
}: BoxesProps & {
  box: AnswerBox;
  inSentence: boolean;
  // The top or the bottom of a fraction drawn in the sentence.
  stacked?: boolean;
}) {
  const values = splitBoxValues(value);
  const text = values[box.id] ?? '';
  const active = activeId === box.id;
  const sentenceBox = `max-w-full rounded-md border-2 border-dashed border-forest-600 px-1 text-center text-forest-800 focus-visible:border-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-600 ${
    active ? 'bg-sage' : 'bg-white'
  }`;
  return (
    <input
      id={boxInputId(box.id)}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={inSentence ? boxName(box) : undefined}
      aria-invalid={boxTextInvalid(text)}
      value={text}
      maxLength={BOX_MAX_LENGTH}
      disabled={disabled}
      // In the sentence: the fill-the-blank dashed box, wide enough for
      // what has been typed so far. In a fraction: room for four digits
      // from the start (plus its own padding and border), so the fraction
      // and the line it is on stay put while the first digits are typed;
      // it only grows for a fifth.
      style={
        stacked
          ? { width: `calc(${String(Math.max(4, text.length))}ch + 1rem)` }
          : inSentence
            ? { width: `${String(Math.max(3, text.length + 1.5))}ch` }
            : undefined
      }
      className={
        stacked
          ? `block h-10 ${sentenceBox}`
          : inSentence
            ? `mx-1 inline-block ${sentenceBox}`
            : `dm-field w-full min-w-0 text-center text-2xl font-semibold tracking-wide focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-600 ${
                active ? 'border-forest-600 bg-sage' : ''
              }`
      }
      onFocus={() => {
        onActive(box.id);
      }}
      onChange={(event) => {
        onChange(
          joinBoxValues(boxes, { ...values, [box.id]: event.target.value }),
        );
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        // Enter moves on to the next box; in the last box it submits.
        const next = boxes[boxes.findIndex((b) => b.id === box.id) + 1];
        if (next) document.getElementById(boxInputId(next.id))?.focus();
        else onSubmit();
      }}
    />
  );
}

// The prompt itself, with each "[a]" marker swapped for its box.
export function BoxesInSentence({
  prompt,
  ...props
}: BoxesProps & { prompt: string }) {
  const values = splitBoxValues(props.value);
  const placed = new Set<string>();
  const findBox = (boxId: string) => props.boxes.find((b) => b.id === boxId);

  // One marker: its box, or (the same marker a second time) what was typed
  // in the first.
  const place = (box: AnswerBox, key: string, stacked: boolean) => {
    if (placed.has(box.id))
      return (
        <span
          key={key}
          aria-hidden="true"
          className={`inline-flex items-center justify-center rounded-md border-2 border-dashed border-forest-600 px-2 text-forest-800 ${
            stacked ? 'h-10 min-w-[calc(4ch+1rem)]' : 'mx-1 min-w-[2.5em]'
          }`}
        >
          {(values[box.id] ?? '').trim() || ' '}
        </span>
      );
    placed.add(box.id);
    return (
      <BoxInput key={key} {...props} box={box} inSentence stacked={stacked} />
    );
  };

  // The top or the bottom of a fraction: a number, or a box.
  const side = (value: string | { boxId: string }, key: string) => {
    if (typeof value === 'string') return value;
    const box = findBox(value.boxId);
    return box ? place(box, key, true) : `[${value.boxId}]`;
  };
  const label = (value: string | { boxId: string }) =>
    typeof value === 'string' ? value : `[${value.boxId}]`;

  return (
    <>
      {splitBoxFractions(
        prompt,
        (boxId) => findBox(boxId) !== undefined,
      ).flatMap((piece, pieceIndex) => {
        const pieceKey = String(pieceIndex);
        // "[a]/15", "3/[a]": drawn stacked like the ordinary fractions
        // beside it, with the box to type into as its top or bottom.
        if (typeof piece !== 'string')
          return [
            <span key={pieceKey} className="whitespace-nowrap">
              <FractionStack
                whole={piece.whole}
                numerator={side(piece.numerator, 'top')}
                denominator={side(piece.denominator, 'bottom')}
                label={`${label(piece.numerator)}/${label(piece.denominator)}`}
                // Read out as "Box a over 15", "2 and Box a over 5".
                spoken={{ afterWhole: ' and ', between: ' over ' }}
                roomy
              />
            </span>,
          ];
        return splitBoxMarkers(piece).map((part, index) => {
          const key = `${pieceKey}-${String(index)}`;
          if (typeof part === 'string')
            return <MathText key={key} text={part} />;
          const box = findBox(part.boxId);
          // Square brackets round a letter that is not one of the boxes:
          // ordinary text.
          if (!box) return `[${part.boxId}]`;
          return place(box, key, false);
        });
      })}
    </>
  );
}

export function LabelledBoxes(props: BoxesProps) {
  return (
    <div className="grid gap-3">
      {props.boxes.map((box) => (
        <div key={box.id} className="grid gap-1">
          <label htmlFor={boxInputId(box.id)} className="font-semibold">
            <MathText text={boxName(box)} />
          </label>
          <BoxInput {...props} box={box} inSentence={false} />
        </div>
      ))}
    </div>
  );
}
